import { ExtendedLineItem } from 'shared/types/cart/LineItem';

export type BuilderDesignGroup = {
  designId: string;
  /** `<design code> · <label>`, the label falling back to `Version N`, or the bare code. */
  title: string;
  /** The builder link that reopens exactly this version; absent when not stamped or not http(s). */
  editUrl?: string;
  items: ExtendedLineItem[];
};

const fieldsOf = (item: ExtendedLineItem) => (item.custom?.fields ?? {}) as Record<string, unknown>;

/**
 * The builder origins an "Edit design" link may point at: `NEXT_PUBLIC_BUILDER_ALLOWED_ORIGINS`, a
 * comma-separated list of EXACT origins (scheme + host + port, e.g. `https://builder.viewrail.com`).
 * Unset or empty means NO origin is trusted, so no Edit design link renders at all: an empty list is
 * never read as "allow any". `designEditUrl` comes from the external builder, and the storefront
 * renders it under its own trusted label, so it must be a known builder before it is linked.
 */
export const allowedBuilderOrigins = (raw = process.env.NEXT_PUBLIC_BUILDER_ALLOWED_ORIGINS): Set<string> =>
  new Set(
    (raw ?? '')
      .split(',')
      .map((o) => o.trim())
      .filter(Boolean)
      .flatMap((o) => {
        try {
          const url = new URL(o);
          // An entry must BE an origin: a path, query or credentials in it is a misconfiguration.
          return url.origin === o.replace(/\/$/, '') && /^https?:$/.test(url.protocol) ? [url.origin] : [];
        } catch {
          return [];
        }
      }),
  );

/** `designEditUrl` rides inside the `productMetadata` JSON; it is linked only on an allowed origin. */
const editUrlOf = (items: ExtendedLineItem[]): string | undefined => {
  const allowed = allowedBuilderOrigins();
  if (allowed.size === 0) return undefined;
  for (const item of items) {
    const raw = fieldsOf(item).productMetadata;
    if (typeof raw !== 'string') continue;
    try {
      const url = JSON.parse(raw)?.designEditUrl;
      if (typeof url !== 'string') continue;
      const parsed = new URL(url);
      if (!parsed.username && !parsed.password && allowed.has(parsed.origin)) return url;
    } catch {
      /* not JSON or not a URL: no link */
    }
  }
  return undefined;
};

/**
 * Split cart rows into builder design groups and flat rows. Rows sharing a `designId` form a group
 * only when the design has 2+ lines OR `designVersion >= 2`; a one-item, unversioned (or v1) design
 * stays a flat row. Rows without a `designId` always stay flat. Order is first appearance.
 */
export const builderDesignGroupsOf = (rows: ExtendedLineItem[]) => {
  const byDesign = new Map<string, ExtendedLineItem[]>();
  rows.forEach((row) => {
    const designId = fieldsOf(row).designId;
    if (typeof designId !== 'string' || !designId) return;
    byDesign.set(designId, [...(byDesign.get(designId) ?? []), row]);
  });

  const groups: BuilderDesignGroup[] = [];
  const grouped = new Set<string>();
  byDesign.forEach((items, designId) => {
    const lines = new Set(items.map((i) => i.lineItemId)).size;
    const version = Math.max(0, ...items.map((i) => Number(fieldsOf(i).designVersion) || 0));
    if (lines < 2 && version < 2) return;
    const label = items.map((i) => fieldsOf(i).designLabel).find((l): l is string => typeof l === 'string' && !!l);
    const detail = label ?? (version > 0 ? `Version ${version}` : undefined);
    groups.push({ designId, title: detail ? `${designId} · ${detail}` : designId, editUrl: editUrlOf(items), items });
    grouped.add(designId);
  });

  const flat = rows.filter((row) => !grouped.has(fieldsOf(row).designId as string));
  return { groups, flat };
};
